export interface ProjectScope {
  orgId: string;
  projectId: string;
}

export interface PartialProjectScope {
  orgId?: unknown;
  projectId?: unknown;
}

function requireNonEmptyString(value: unknown, fieldName: string): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`${fieldName} is required`);
  }
  return value.trim();
}

function requireOrgId(value: unknown): string {
  return requireNonEmptyString(value, 'orgId');
}

function requireProjectId(value: unknown): string {
  return requireNonEmptyString(value, 'projectId');
}

export function requireProjectScope(value: PartialProjectScope): ProjectScope {
  return {
    orgId: requireOrgId(value.orgId),
    projectId: requireProjectId(value.projectId),
  };
}

export function buildProjectCollectionPath(orgId: string): string {
  return `organizations/${requireOrgId(orgId)}/projects`;
}

export function buildProjectDocPath(scope: ProjectScope): string {
  const checked = requireProjectScope(scope);
  return `${buildProjectCollectionPath(checked.orgId)}/${checked.projectId}`;
}

export function buildProjectSubcollectionPath(
  scope: ProjectScope,
  collectionName: string
): string {
  const checked = requireProjectScope(scope);
  return `${buildProjectDocPath(checked)}/${requireNonEmptyString(collectionName, 'collectionName')}`;
}

export function buildScriptJobCollectionPath(scope: ProjectScope): string {
  return buildProjectSubcollectionPath(scope, 'scriptJobs');
}

export function buildScriptJobDocPath(scope: ProjectScope, jobId: string): string {
  return `${buildScriptJobCollectionPath(scope)}/${requireNonEmptyString(jobId, 'jobId')}`;
}

export function buildProjectMemberPath(scope: ProjectScope, userId: string): string {
  return `${buildProjectSubcollectionPath(scope, 'members')}/${requireNonEmptyString(userId, 'userId')}`;
}

export function extractProjectScopeFromMemberPath(path: string): ProjectScope | null {
  const parts = path.split('/').filter(Boolean);
  if (
    parts.length === 6 &&
    parts[0] === 'organizations' &&
    parts[2] === 'projects' &&
    parts[4] === 'members'
  ) {
    return { orgId: parts[1]!, projectId: parts[3]! };
  }
  return null;
}
